import {ApolloError} from '@apollo/client';
import {
    CartAddressInput,
    CustomerAddress,
    CustomerAddressInput,
    CustomerFaqData,
    CustomerOrderSortableField,
    CustomerOrders,
    ScopeTypeEnum,
    SortEnum,
    WishlistItem,
} from '../../../__generated__/__types__';
import {
    useChangeCustomerPasswordMutation,
    useCreateCustomerAddressMutation,
    useCustomerAddressesQuery,
    useCustomerFaqQuery,
    useCustomerListAddressesQuery,
    useCustomerRankQuery,
    useCustomerWishlistQuery,
    useDeleteCustomerAddressMutation,
    useOrderDetailLazyQuery,
    useOrdersQuery,
    useUpdateCustomerAddressMutation,
    useUpdateCustomerMutation,
} from '@/framework/gql/customer.graphql';

import {toast} from 'react-toastify';
import {toastSuccessMessage} from '@/_application/core/components/ui/toast/toast';
import {useTranslation} from 'next-i18next';

export interface ListAddressType {
    default_billing: boolean;
    default_shipping: boolean;
    id: number;
}

export function useCustomerAddresses() {
    const {
        data,
        loading: isLoading,
        error,
    } = useCustomerAddressesQuery({
        fetchPolicy: 'network-only',
        onError: (err: ApolloError) => {
            console.log(err);
        },
    });
    return {addresses: (data?.customer?.addresses as CustomerAddress[]) || [], isLoading, error};
}

export const isAddressSameLocalInput = (cartAddressInput: CartAddressInput | null, address: CustomerAddress) => {
    return (
        cartAddressInput &&
        cartAddressInput.firstname &&
        cartAddressInput.lastname === address.lastname &&
        cartAddressInput.telephone === address.telephone &&
        cartAddressInput.street.join(',') === address.street?.join(',') &&
        cartAddressInput.region === address.region?.region_code &&
        cartAddressInput.district === address.district &&
        cartAddressInput.ward === address.ward
    );
};

export const isExistingAddress = (cartAddressInput: CartAddressInput | null, addresses: CustomerAddress[]): boolean => {
    if (!cartAddressInput) return false;
    return addresses.some(address => isAddressSameLocalInput(cartAddressInput, address));
};

export function useGetCustomerOrders(scope: ScopeTypeEnum, currentPage: number, pageSize: number) {
    const {
        data,
        loading: isLoading,
        error,
    } = useOrdersQuery({
        variables: {
            scope,
            currentPage,
            pageSize,
            sort: {
                sort_direction: SortEnum.Desc,
                sort_field: CustomerOrderSortableField.CreatedAt,
            },
        },
    });

    return {
        data: (data?.customer?.orders ?? {}) as CustomerOrders,
        isLoading,
        error,
    };
}

export function useGetCustomerOrderDetail(scope: ScopeTypeEnum, orderId: string) {
    const [fetchCustomerOrderDetail, {data, loading: isLoading, error}] = useOrderDetailLazyQuery();

    const fetchData = async () => {
        const response = await fetchCustomerOrderDetail({
            variables: {
                scope,
                filter: {
                    number: {
                        eq: orderId,
                    },
                },
            },
        });

        return response.data?.customer?.orders?.items[0];
    };

    return {
        data: data?.customer?.orders?.items[0],
        isLoading,
        error,
        fetchData,
    };
}

export function useCustomerFaq(currentPage: number, pageSize: number) {
    const {
        data,
        loading: isLoading,
        error,
    } = useCustomerFaqQuery({
        variables: {
            currentPage,
            pageSize,
        },
    });

    return {
        data: (data?.customer?.faq?.data ?? []) as CustomerFaqData[],
        pagination: data?.customer?.faq?.pagination_metadata,
        isLoading,
        error,
    };
}

export function useChangeCustomerPassword() {
    const [changeCustomerPasswordMutation, {loading: isLoading, error}] = useChangeCustomerPasswordMutation({
        onCompleted: () => {},
        onError: (error: ApolloError) => {
            if (error?.message) {
                toast.error(error?.message, {
                    toastId: 'error-change-password',
                });
            } else {
                toast.error('Không thể cập nhật thông tin!', {
                    toastId: 'error-change-password',
                });
            }

            return error.message;
        },
    });

    const changeCustomerPassword = (currentPassword: string, newPassword: string) => {
        return changeCustomerPasswordMutation({
            variables: {
                currentPassword,
                newPassword,
            },
        });
    };

    return {
        changeCustomerPassword,
        isLoading,
        error,
    };
}

export function useChangeCustomerName() {
    const [updateCustomerMutation, {loading: isLoading, error}] = useUpdateCustomerMutation({
        onCompleted: () => {},
        onError: (error: ApolloError) => {
            if (error?.message) {
                toast.error(error?.message, {
                    toastId: 'error-change-customer-name',
                });
            } else {
                toast.error('Không thể cập nhật thông tin!', {
                    toastId: 'error-change-customer-name',
                });
            }

            return error.message;
        },
    });

    const updateCustomerName = (firstname: string, lastname: string) => {
        return updateCustomerMutation({
            variables: {
                input: {
                    firstname,
                    lastname,
                },
            },
        });
    };

    return {
        updateCustomerName,
        isLoading,
        error,
    };
}

export function useCustomerRank() {
    const {data, loading: isLoading, error} = useCustomerRankQuery();

    return {
        data: data?.customer?.rank ?? '',
        isLoading,
        error,
    };
}

export function useCreateCustomerAddress() {
    const {t} = useTranslation();
    const [createCustomerAddressMutation, {loading: isLoading, error}] = useCreateCustomerAddressMutation({
        onCompleted: () => {
            toastSuccessMessage(t('text-create-address-success'));
        },
        onError: (error: ApolloError) => {
            toast.error(error?.message, {
                toastId: 'error',
            });
        },
    });

    const createCustomerAddress = async (input: CustomerAddressInput) => {
        return createCustomerAddressMutation({
            refetchQueries: ['CustomerAddresses'],
            variables: {
                input,
            },
        });
    };

    return {
        createCustomerAddress,
        isLoading,
        error,
    };
}

export function useUpdateCustomerAddress() {
    const {t} = useTranslation();
    const [updateCustomerAddressMutation, {loading: isLoading, error}] = useUpdateCustomerAddressMutation({
        onCompleted: () => {
            toastSuccessMessage(t('text-update-address-success'));
        },
        onError: (error: ApolloError) => {
            toast.error(error?.message, {
                toastId: 'error',
            });
        },
    });

    const updateCustomerAddress = async (addressId: number, input: CustomerAddressInput) => {
        return updateCustomerAddressMutation({
            refetchQueries: ['CustomerAddresses'],
            variables: {
                updateCustomerAddressId: addressId,
                input,
            },
        });
    };

    return {
        updateCustomerAddress,
        isLoading,
        error,
    };
}

export function useDeleteCustomerAddress() {
    const {t} = useTranslation();
    const [deleteCustomerAddressMutation, {loading: isLoading, error}] = useDeleteCustomerAddressMutation({
        onCompleted: () => {
            toastSuccessMessage(t('text-delete-address-success'));
        },
        onError: (error: ApolloError) => {
            toast.error(error?.message, {
                toastId: 'error',
            });
        },
    });

    const deleteCustomerAddress = async (addressId: number) => {
        return deleteCustomerAddressMutation({
            refetchQueries: ['CustomerAddresses'],
            variables: {
                deleteCustomerAddressId: addressId,
            },
        });
    };

    return {
        deleteCustomerAddress,
        isLoading,
        error,
    };
}

export function useCustomerWishlist() {
    const {data, loading: isLoading, error} = useCustomerWishlistQuery();

    return {
        data: (data?.customer?.wishlist?.items ?? []) as WishlistItem[],
        isLoading,
        error,
    };
}

export function useCustomerListAddress() {
    const {data, loading: isLoading, error} = useCustomerListAddressesQuery();

    return {
        data: data?.customer?.addresses ?? [],
        isLoading,
        error,
    };
}
